import React, { useCallback, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import { Button } from 'ui-components/components/button';
import { Tooltip } from 'ui-components/components/tooltip';
import {
  NotificationService,
  NOTIFICATION_DURATIONS,
  NOTIFICATION_TYPE_ERROR,
  NOTIFICATION_TYPE_SUCCESS,
} from 'ui-components/services/notification-service';
import { THEME_CONSTANTS } from 'ui-components/services/button-theme-service';

import { flagAddressForFraud } from './flag-address.api';
import { FlagAddressModal } from './flag-address-modal';
import style from './flag-address-button.module.less';
import { messages } from './messages';
import { FRAUD_INDICATOR_CODES } from '../../../constants/fraud-constants';
import { useModal } from '../../../hooks/use-modal';
import Global from '../../../store/global-selectors';
import { useSalesOrderIdContext } from '../../context/sales-order-id-context-provider';
import { silentlyGetOrderDetails } from '../../../store/orders/order-details/orders.details.actions';
import { silentRequestGetOrders } from '../../../store/orders/orders.actions';

import type { FraudIndicatorFromBackend } from '../../../types/fraud-detection';

type FlagAddressButtonProps = {
  fulfillmentPlanId: string;
};

export const FlagAddressButton = ({ fulfillmentPlanId }: FlagAddressButtonProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const dispatch = useDispatch();
  const salesOrderId = useSalesOrderIdContext();
  const intl = useIntl();
  const { isOpen, open, close } = useModal();

  const fulfillmentPlan = useSelector((state) => Global.selectEntitiesFulfillmentPlanById(state, { fulfillmentPlanId }));

  const hasExactBlacklistIndicator = useMemo(() => {
    const fraudDetection = fulfillmentPlan?.fraudDetection;

    if (!fraudDetection?.indicators || !Array.isArray(fraudDetection.indicators)) {
      return false;
    }

    return fraudDetection.indicators.some(
      (indicator: FraudIndicatorFromBackend) => indicator.fraudIndicatorCode === FRAUD_INDICATOR_CODES.BLACKLIST_EXACT,
    );
  }, [fulfillmentPlan]);

  const handleFlagAddress = useCallback(
    async (reason: string) => {
      setIsLoading(true);
      try {
        await flagAddressForFraud(fulfillmentPlanId, reason);
        NotificationService.showNotification({
          notificationType: NOTIFICATION_TYPE_SUCCESS,
          title: <FormattedMessage {...messages.flagAddressSuccess} />,
          duration: NOTIFICATION_DURATIONS.SHORT,
        });
        dispatch(silentlyGetOrderDetails(salesOrderId));
        dispatch(silentRequestGetOrders());
        close();
      } catch {
        NotificationService.showNotification({
          notificationType: NOTIFICATION_TYPE_ERROR,
          title: <FormattedMessage {...messages.flagAddressError} />,
          duration: NOTIFICATION_DURATIONS.SHORT,
        });
      } finally {
        setIsLoading(false);
      }
    },
    [fulfillmentPlanId, dispatch, salesOrderId, close],
  );

  const isDisabled = isLoading || hasExactBlacklistIndicator;

  const tooltipText = hasExactBlacklistIndicator
    ? intl.formatMessage(messages.alreadyBlacklistedTooltip)
    : intl.formatMessage(messages.flagAddressTooltip);

  return (
    <>
      <Tooltip text={tooltipText}>
        <span className={style['flag-address-button-wrapper']}>
          <Button className={style['flag-address-button']} disabled={isDisabled} onClick={open} theme={THEME_CONSTANTS.danger}>
            <FormattedMessage {...messages.flagAddressButton} />
          </Button>
        </span>
      </Tooltip>
      {isOpen && <FlagAddressModal isFlagging={isLoading} onClose={close} onFlag={handleFlagAddress} />}
    </>
  );
};
